import React, { useEffect, useState } from "react";

import {
    HeartOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    UserOutlined,
    DownOutlined,
    UpOutlined
} from "@ant-design/icons";

import {
    canSyncRehabData,
    getActiveRehabProfileId,
    getRehabHistory,
    saveRehabData,
    deleteRehabData
} from "../../api/rehabTracking";

import {
    getPatientProfiles
} from "../../api/patientProfile";


export default function NhatKyDau() {

    const [painLevel, setPainLevel] = useState(0);
    const [location, setLocation] = useState("");
    const [side, setSide] = useState("");
    const [activity, setActivity] = useState("nghi-ngoi");
    const [note, setNote] = useState("");

    const [showDetail, setShowDetail] = useState(false);

    const [radiation, setRadiation] = useState("");
    const [painQuality, setPainQuality] = useState("");
    const [painPattern, setPainPattern] = useState("");
    const [aggravatingFactors, setAggravatingFactors] = useState("");
    const [relievingFactors, setRelievingFactors] = useState("");
    const [sleepEffect, setSleepEffect] = useState("");
    const [numbness, setNumbness] = useState(false);
    const [weakness, setWeakness] = useState(false);
    const [paresthesia, setParesthesia] = useState(false);
    const [treatmentUsed, setTreatmentUsed] = useState("");
    const [responseToTreatment, setResponseToTreatment] = useState("");

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [activeProfile, setActiveProfile] = useState(null);


    const getActivityLabel = (value) => {

        switch (value) {

            case "nghi-ngoi":
                return "Khi nghỉ ngơi";

            case "di-bo":
                return "Khi đi bộ";

            case "van-dong":
                return "Khi vận động";

            case "tap-phcn":
                return "Khi tập phục hồi chức năng";

            case "sau-tap":
                return "Sau khi tập";

            case "ban-dem":
                return "Ban đêm";

            default:
                return "Khác";
        }
    };


    const getSideLabel = (value) => {

        switch (value) {

            case "phai":
                return "Phải";

            case "trai":
                return "Trái";

            case "hai-ben":
                return "Hai bên";

            case "giua":
                return "Giữa";

            default:
                return "";
        }
    };


    const getPatternLabel = (value) => {

        switch (value) {

            case "lien-tuc":
                return "Liên tục";

            case "tung-luc":
                return "Từng lúc";

            default:
                return "";
        }
    };


    /*
     * Chỉ dùng màu để phân biệt trực quan.
     * Không gán các mức nhẹ / vừa / nặng.
     */
    const getPainColor = (level) => {

        if (Number(level) === 0) {
            return "text-emerald-700 bg-emerald-50";
        }

        return "text-rose-600 bg-rose-50";
    };


    /*
     * Không tự chia ngưỡng đau nhẹ / vừa / nặng.
     */
    const getPainLabel = (level) => {

        if (Number(level) === 0) {
            return "Không đau";
        }

        return `Mức đau tự đánh giá: ${level}/10`;
    };


    const formatDateTime = (value) => {

        if (!value) {

            return {
                date: "",
                time: ""
            };
        }


        const dateObject = new Date(value);


        if (Number.isNaN(dateObject.getTime())) {

            return {
                date: "",
                time: ""
            };
        }


        return {

            date: dateObject.toLocaleDateString("vi-VN"),

            time: dateObject.toLocaleTimeString(
                "vi-VN",
                {
                    hour: "2-digit",
                    minute: "2-digit"
                }
            )
        };
    };


    const loadActiveProfile = async () => {

        try {

            if (!canSyncRehabData()) {

                setActiveProfile(null);
                return;
            }


            const activeId = getActiveRehabProfileId();


            if (!activeId) {

                setActiveProfile(null);
                return;
            }


            const profiles = await getPatientProfiles();


            const found =
                Array.isArray(profiles)
                    ? profiles.find(
                        (item) =>
                            Number(item.profile_id) === Number(activeId)
                    )
                    : null;


            setActiveProfile(found || null);

        } catch (error) {

            console.error(
                "Không thể tải hồ sơ đang chọn:",
                error
            );

            setActiveProfile(null);
        }
    };


    const loadHistory = async () => {

        setLoading(true);
        setMessage("");


        try {

            if (!canSyncRehabData()) {

                setHistory([]);

                setMessage(
                    "Vui lòng đăng nhập để lưu và xem dữ liệu phục hồi chức năng."
                );

                return;
            }


            const profileId = getActiveRehabProfileId();


            if (!profileId) {

                setHistory([]);

                setMessage(
                    "Vui lòng chọn hồ sơ đang theo dõi trước khi sử dụng công cụ."
                );

                return;
            }


            const data = await getRehabHistory("nhat_ky_dau");


            const formatted =
                Array.isArray(data)
                    ? data.map((item) => {

                        const tracking =
                            item.tracking_data || {};


                        const dateTime =
                            formatDateTime(item.created_at);


                        return {

                            id:
                                item.health_tracking_id,

                            painLevel:
                                Number(
                                    tracking.painLevel ??
                                    tracking.pain_level ??
                                    tracking.pain_intensity ??
                                    0
                                ),

                            scaleType:
                                tracking.scaleType ||
                                tracking.scale_type ||
                                "NRS",

                            location:
                                tracking.location || "",

                            side:
                                tracking.side || "",

                            sideLabel:
                                tracking.sideLabel ||
                                tracking.side_label ||
                                getSideLabel(tracking.side),

                            activity:
                                tracking.activity || "",

                            activityLabel:
                                tracking.activityLabel ||
                                tracking.activity_label ||
                                getActivityLabel(
                                    tracking.activity
                                ),

                            radiation:
                                tracking.radiation || "",

                            painQuality:
                                tracking.painQuality ||
                                tracking.pain_quality ||
                                "",

                            painPattern:
                                tracking.painPattern ||
                                tracking.continuous_or_intermitent ||
                                "",

                            painPatternLabel:
                                tracking.painPatternLabel ||
                                tracking.pain_pattern_label ||
                                getPatternLabel(
                                    tracking.painPattern ||
                                    tracking.continuous_or_intermitent
                                ),

                            aggravatingFactors:
                                tracking.aggravatingFactors ||
                                tracking.aggravating_factors ||
                                "",

                            relievingFactors:
                                tracking.relievingFactors ||
                                tracking.relieving_factors ||
                                "",

                            sleepEffect:
                                tracking.sleepEffect ||
                                tracking.sleep_effect ||
                                "",

                            numbness:
                                Boolean(tracking.numbness),

                            weakness:
                                Boolean(tracking.weakness),

                            paresthesia:
                                Boolean(tracking.paresthesia),

                            treatmentUsed:
                                tracking.treatmentUsed ||
                                tracking.treatment_used ||
                                "",

                            responseToTreatment:
                                tracking.responseToTreatment ||
                                tracking.response_to_treatment ||
                                "",

                            note:
                                tracking.note ||
                                tracking.notes ||
                                "",

                            date:
                                tracking.date ||
                                dateTime.date,

                            time:
                                tracking.time ||
                                dateTime.time
                        };
                    })
                    : [];


            setHistory(formatted);

        } catch (error) {

            console.error(
                "Không thể tải nhật ký đau:",
                error
            );


            setHistory([]);


            setMessage(
                error?.message ||
                "Không thể tải nhật ký đau."
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadActiveProfile();
        loadHistory();

    }, []);


    const resetForm = () => {

        setPainLevel(0);
        setLocation("");
        setSide("");
        setActivity("nghi-ngoi");
        setNote("");

        setRadiation("");
        setPainQuality("");
        setPainPattern("");
        setAggravatingFactors("");
        setRelievingFactors("");
        setSleepEffect("");

        setNumbness(false);
        setWeakness(false);
        setParesthesia(false);

        setTreatmentUsed("");
        setResponseToTreatment("");

        setShowDetail(false);
    };


    const handleSave = async () => {

        if (!canSyncRehabData()) {

            alert(
                "Vui lòng đăng nhập trước khi lưu dữ liệu."
            );

            return;
        }


        if (!getActiveRehabProfileId()) {

            alert(
                "Vui lòng chọn hồ sơ đang theo dõi trước."
            );

            return;
        }


        if (!location.trim()) {

            alert(
                "Vui lòng nhập vị trí đau."
            );

            return;
        }


        const now = new Date();


        const trackingData = {

            scaleType: "NRS",

            painLevel: Number(painLevel),

            location: location.trim(),

            side,

            sideLabel: getSideLabel(side),

            activity,

            activityLabel:
                getActivityLabel(activity),

            radiation:
                radiation.trim(),

            painQuality:
                painQuality.trim(),

            painPattern,

            painPatternLabel:
                getPatternLabel(painPattern),

            aggravatingFactors:
                aggravatingFactors.trim(),

            relievingFactors:
                relievingFactors.trim(),

            sleepEffect:
                sleepEffect.trim(),

            numbness,

            weakness,

            paresthesia,

            treatmentUsed:
                treatmentUsed.trim(),

            responseToTreatment:
                responseToTreatment.trim(),

            note:
                note.trim(),

            date:
                now.toLocaleDateString("vi-VN"),

            time:
                now.toLocaleTimeString(
                    "vi-VN",
                    {
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                )
        };


        setSaving(true);
        setMessage("");


        try {

            await saveRehabData(
                "nhat_ky_dau",
                trackingData
            );


            resetForm();

            await loadHistory();


            setMessage(
                "Đã lưu nhật ký đau."
            );

        } catch (error) {

            console.error(
                "Không thể lưu nhật ký đau:",
                error
            );


            setMessage(
                error?.message ||
                "Không thể lưu nhật ký đau."
            );

        } finally {

            setSaving(false);
        }
    };


    const handleDelete = async (id) => {

        const confirmed =
            window.confirm(
                "Bạn có chắc muốn xóa lần ghi nhận này?"
            );


        if (!confirmed) {
            return;
        }


        try {

            await deleteRehabData(id);


            setHistory(
                (current) =>
                    current.filter(
                        (item) =>
                            Number(item.id) !== Number(id)
                    )
            );


            setMessage(
                "Đã xóa lần ghi nhận."
            );

        } catch (error) {

            console.error(
                "Không thể xóa nhật ký đau:",
                error
            );


            setMessage(
                error?.message ||
                "Không thể xóa dữ liệu."
            );
        }
    };


    const hasDetail = (item) => {

        return Boolean(
            item.side ||
            item.radiation ||
            item.painQuality ||
            item.painPattern ||
            item.aggravatingFactors ||
            item.relievingFactors ||
            item.sleepEffect ||
            item.numbness ||
            item.weakness ||
            item.paresthesia ||
            item.treatmentUsed ||
            item.responseToTreatment
        );
    };


    return (

        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            <a
                href="/tienich/phuc-hoi-chuc-nang"
                className="inline-flex items-center text-emerald-600 font-semibold text-sm mb-4"
            >

                <ArrowLeftOutlined className="mr-2" />

                Phục hồi chức năng

            </a>


            {/* HEADER */}

            <div className="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">

                        <HeartOutlined />

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            PHỤC HỒI CHỨC NĂNG

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold">

                            Nhật ký đau

                        </h1>

                    </div>

                </div>


                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal max-w-3xl">

                    Ghi nhận mức đau tự đánh giá và các đặc điểm liên quan
                    để theo dõi sự thay đổi theo thời gian.

                </p>

            </div>


            {/* HỒ SƠ */}

            {activeProfile ? (

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4">

                    <div className="flex items-center">

                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">

                            <UserOutlined />

                        </div>


                        <div className="ml-3">

                            <p className="text-[11px] font-bold text-emerald-700 uppercase">

                                Đang ghi dữ liệu cho

                            </p>


                            <p className="font-bold text-gray-900">

                                {activeProfile.profile_name}

                            </p>


                            {activeProfile.relationship && (

                                <p className="text-xs text-gray-500 mt-0.5">

                                    {activeProfile.relationship}

                                </p>

                            )}

                        </div>

                    </div>

                </div>

            ) : (

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-4">

                    <p className="font-semibold text-gray-800 text-sm">

                        Chưa chọn hồ sơ theo dõi

                    </p>


                    <p className="text-[13px] text-gray-600 mt-1">

                        Quay lại trang Phục hồi chức năng để chọn hoặc tạo hồ sơ trước khi lưu dữ liệu.

                    </p>


                    <a
                        href="/tienich/phuc-hoi-chuc-nang"
                        className="inline-block mt-2 text-emerald-600 font-semibold text-sm"
                    >

                        Chọn hồ sơ

                    </a>

                </div>

            )}


            {message && (

                <div className="bg-gray-50 border rounded-xl px-4 py-3 mt-4 text-[13px] text-gray-600">

                    {message}

                </div>

            )}


            {/* FORM */}

            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Ghi nhận mức đau

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    NRS 0–10 dựa trên mức đau người dùng tự cảm nhận.

                </p>


                {/* NRS */}

                <div className="mt-6">

                    <div className="flex items-center justify-between">

                        <label className="text-sm font-semibold text-gray-700">

                            Mức độ đau

                        </label>


                        <div
                            className={`px-3 py-1 rounded-lg font-bold text-sm ${getPainColor(
                                Number(painLevel)
                            )}`}
                        >

                            {painLevel}/10

                        </div>

                    </div>


                    <input
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={painLevel}
                        onChange={(e) =>
                            setPainLevel(
                                Number(e.target.value)
                            )
                        }
                        className="w-full mt-4"
                    />


                    <div className="flex justify-between text-[11px] text-gray-400 mt-1">

                        <span>0 - Không đau</span>

                        <span>10 - Đau cao nhất</span>

                    </div>


                    <div
                        className={`rounded-xl px-4 py-3 mt-4 ${getPainColor(
                            Number(painLevel)
                        )}`}
                    >

                        <p className="font-bold text-sm">

                            {getPainLabel(
                                Number(painLevel)
                            )}

                        </p>

                    </div>

                </div>


                {/* VỊ TRÍ */}

                <div className="mt-5">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Vị trí đau

                    </label>


                    <input
                        type="text"
                        value={location}
                        onChange={(e) =>
                            setLocation(e.target.value)
                        }
                        placeholder="Ví dụ: vai, lưng, gối..."
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-emerald-500"
                    />

                </div>


                {/* BÊN */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Bên đau

                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>

                    </label>


                    <select
                        value={side}
                        onChange={(e) =>
                            setSide(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-emerald-500"
                    >

                        <option value="">
                            Chưa xác định
                        </option>

                        <option value="phai">
                            Phải
                        </option>

                        <option value="trai">
                            Trái
                        </option>

                        <option value="hai-ben">
                            Hai bên
                        </option>

                        <option value="giua">
                            Giữa
                        </option>

                    </select>

                </div>


                {/* HOẠT ĐỘNG */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Hoàn cảnh ghi nhận đau

                    </label>


                    <select
                        value={activity}
                        onChange={(e) =>
                            setActivity(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-emerald-500"
                    >

                        <option value="nghi-ngoi">
                            Khi nghỉ ngơi
                        </option>

                        <option value="di-bo">
                            Khi đi bộ
                        </option>

                        <option value="van-dong">
                            Khi vận động
                        </option>

                        <option value="tap-phcn">
                            Khi tập phục hồi chức năng
                        </option>

                        <option value="sau-tap">
                            Sau khi tập
                        </option>

                        <option value="ban-dem">
                            Ban đêm
                        </option>

                        <option value="khac">
                            Khác
                        </option>

                    </select>

                </div>


                {/* CHI TIẾT */}

                <button
                    type="button"
                    onClick={() =>
                        setShowDetail(!showDetail)
                    }
                    className="w-full mt-5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-xl px-4 py-3 font-semibold flex items-center justify-between"
                >

                    <span>
                        + Ghi nhận chi tiết
                    </span>

                    {showDetail
                        ? <UpOutlined />
                        : <DownOutlined />
                    }

                </button>


                {showDetail && (

                    <div className="mt-4 bg-gray-50 rounded-2xl p-4 border">


                        <div>

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Đau lan đến đâu?

                            </label>

                            <input
                                type="text"
                                value={radiation}
                                onChange={(e) =>
                                    setRadiation(e.target.value)
                                }
                                placeholder="Ví dụ: từ thắt lưng lan xuống chân..."
                                className="w-full h-12 bg-white border rounded-xl px-3 outline-none focus:border-emerald-500"
                            />

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Tính chất đau

                            </label>

                            <input
                                type="text"
                                value={painQuality}
                                onChange={(e) =>
                                    setPainQuality(e.target.value)
                                }
                                placeholder="Mô tả cảm giác đau..."
                                className="w-full h-12 bg-white border rounded-xl px-3 outline-none focus:border-emerald-500"
                            />

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Kiểu xuất hiện

                            </label>

                            <select
                                value={painPattern}
                                onChange={(e) =>
                                    setPainPattern(e.target.value)
                                }
                                className="w-full h-12 bg-white border rounded-xl px-3 outline-none focus:border-emerald-500"
                            >

                                <option value="">
                                    Chưa ghi nhận
                                </option>

                                <option value="lien-tuc">
                                    Liên tục
                                </option>

                                <option value="tung-luc">
                                    Từng lúc
                                </option>

                            </select>

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Yếu tố làm đau tăng

                            </label>

                            <textarea
                                value={aggravatingFactors}
                                onChange={(e) =>
                                    setAggravatingFactors(
                                        e.target.value
                                    )
                                }
                                rows={2}
                                placeholder="Những yếu tố làm đau tăng..."
                                className="w-full bg-white border rounded-xl px-3 py-3 outline-none focus:border-emerald-500 resize-none"
                            />

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Yếu tố làm đau giảm

                            </label>

                            <textarea
                                value={relievingFactors}
                                onChange={(e) =>
                                    setRelievingFactors(
                                        e.target.value
                                    )
                                }
                                rows={2}
                                placeholder="Những yếu tố giúp giảm đau..."
                                className="w-full bg-white border rounded-xl px-3 py-3 outline-none focus:border-emerald-500 resize-none"
                            />

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Ảnh hưởng đến giấc ngủ

                            </label>

                            <input
                                type="text"
                                value={sleepEffect}
                                onChange={(e) =>
                                    setSleepEffect(e.target.value)
                                }
                                placeholder="Ví dụ: khó ngủ, thức giấc..."
                                className="w-full h-12 bg-white border rounded-xl px-3 outline-none focus:border-emerald-500"
                            />

                        </div>


                        <div className="mt-5">

                            <p className="text-sm font-semibold text-gray-700 mb-3">

                                Triệu chứng đi kèm

                            </p>


                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                                <label className="bg-white border rounded-xl px-4 py-3 flex items-center cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={numbness}
                                        onChange={(e) =>
                                            setNumbness(
                                                e.target.checked
                                            )
                                        }
                                        className="mr-3"
                                    />

                                    Tê

                                </label>


                                <label className="bg-white border rounded-xl px-4 py-3 flex items-center cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={weakness}
                                        onChange={(e) =>
                                            setWeakness(
                                                e.target.checked
                                            )
                                        }
                                        className="mr-3"
                                    />

                                    Yếu

                                </label>


                                <label className="bg-white border rounded-xl px-4 py-3 flex items-center cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={paresthesia}
                                        onChange={(e) =>
                                            setParesthesia(
                                                e.target.checked
                                            )
                                        }
                                        className="mr-3"
                                    />

                                    Dị cảm

                                </label>

                            </div>

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Điều trị/biện pháp đã sử dụng

                            </label>

                            <textarea
                                value={treatmentUsed}
                                onChange={(e) =>
                                    setTreatmentUsed(
                                        e.target.value
                                    )
                                }
                                rows={2}
                                placeholder="Ghi lại biện pháp đã sử dụng nếu có..."
                                className="w-full bg-white border rounded-xl px-3 py-3 outline-none focus:border-emerald-500 resize-none"
                            />

                        </div>


                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Đáp ứng sau đó

                            </label>

                            <textarea
                                value={responseToTreatment}
                                onChange={(e) =>
                                    setResponseToTreatment(
                                        e.target.value
                                    )
                                }
                                rows={2}
                                placeholder="Ghi lại thay đổi được quan sát hoặc cảm nhận..."
                                className="w-full bg-white border rounded-xl px-3 py-3 outline-none focus:border-emerald-500 resize-none"
                            />

                        </div>

                    </div>

                )}


                {/* GHI CHÚ */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Ghi chú

                        <span className="font-normal text-gray-400 ml-1">

                            (không bắt buộc)

                        </span>

                    </label>


                    <textarea
                        value={note}
                        onChange={(e) =>
                            setNote(e.target.value)
                        }
                        placeholder="Thông tin khác muốn ghi lại..."
                        rows={3}
                        className="w-full border rounded-xl px-3 py-3 outline-none focus:border-emerald-500 resize-none"
                    />

                </div>


                <button
                    type="button"
                    onClick={handleSave}
                    disabled={
                        saving ||
                        !activeProfile
                    }
                    className="w-full mt-5 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu nhật ký"
                    }

                </button>

            </div>


            {/* LỊCH SỬ */}

            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Lịch sử đau

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Các lần ghi nhận của hồ sơ đang được chọn.

                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">

                            Đang tải dữ liệu...

                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <HeartOutlined className="text-3xl text-gray-300" />


                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có dữ liệu

                        </p>


                        <p className="text-sm text-gray-500 mt-1">

                            Nhật ký đau của hồ sơ này sẽ xuất hiện tại đây.

                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4"
                            >

                                <div className="flex items-start justify-between">

                                    <div className="flex-1 pr-3">

                                        <div className="flex flex-wrap items-center gap-2">

                                            <div
                                                className={`px-3 py-1 rounded-lg font-bold text-sm ${getPainColor(
                                                    Number(item.painLevel)
                                                )}`}
                                            >

                                                NRS {item.painLevel}/10

                                            </div>


                                            <span className="text-sm font-semibold text-gray-800">

                                                {item.location}

                                                {item.sideLabel
                                                    ? ` - ${item.sideLabel}`
                                                    : ""
                                                }

                                            </span>

                                        </div>


                                        <p className="text-[13px] text-gray-600 mt-2">

                                            {item.activityLabel}

                                        </p>


                                        <p className="text-xs text-gray-400 mt-1">

                                            {item.time} - {item.date}

                                        </p>


                                        {hasDetail(item) && (

                                            <div className="bg-gray-50 rounded-xl px-3 py-3 mt-3 space-y-1">

                                                {item.radiation && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Lan đau:</strong> {item.radiation}
                                                    </p>
                                                )}

                                                {item.painQuality && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Tính chất:</strong> {item.painQuality}
                                                    </p>
                                                )}

                                                {item.painPatternLabel && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Kiểu xuất hiện:</strong> {item.painPatternLabel}
                                                    </p>
                                                )}

                                                {item.aggravatingFactors && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Tăng khi:</strong> {item.aggravatingFactors}
                                                    </p>
                                                )}

                                                {item.relievingFactors && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Giảm khi:</strong> {item.relievingFactors}
                                                    </p>
                                                )}

                                                {item.sleepEffect && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Giấc ngủ:</strong> {item.sleepEffect}
                                                    </p>
                                                )}

                                                {(item.numbness ||
                                                    item.weakness ||
                                                    item.paresthesia) && (

                                                    <p className="text-[13px] text-gray-600">

                                                        <strong>Đi kèm:</strong>{" "}

                                                        {[
                                                            item.numbness ? "Tê" : "",
                                                            item.weakness ? "Yếu" : "",
                                                            item.paresthesia ? "Dị cảm" : ""
                                                        ]
                                                            .filter(Boolean)
                                                            .join(", ")}

                                                    </p>

                                                )}

                                                {item.treatmentUsed && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Đã sử dụng:</strong> {item.treatmentUsed}
                                                    </p>
                                                )}

                                                {item.responseToTreatment && (
                                                    <p className="text-[13px] text-gray-600">
                                                        <strong>Đáp ứng:</strong> {item.responseToTreatment}
                                                    </p>
                                                )}

                                            </div>

                                        )}


                                        {item.note && (

                                            <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                                <p className="text-[13px] text-gray-500">

                                                    {item.note}

                                                </p>

                                            </div>

                                        )}

                                    </div>


                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleDelete(item.id)
                                        }
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                        aria-label="Xóa nhật ký đau"
                                    >

                                        <DeleteOutlined />

                                    </button>

                                </div>

                            </div>

                        ))}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}

            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">

                    Về Nhật ký đau

                </p>


                <p className="text-[13px] text-gray-600 leading-5 mt-1">

                    NRS 0–10 ghi nhận mức đau do người dùng tự đánh giá.
                    Điểm 0 tương ứng không đau và 10 là mức đau cao nhất
                    người dùng có thể hình dung hoặc cảm nhận theo thang này.

                </p>


                <p className="text-[13px] text-gray-500 leading-5 mt-2">

                    Website không tự phân loại điểm thành đau nhẹ, vừa hoặc nặng.
                    Nhật ký được sử dụng để theo dõi sự thay đổi theo thời gian
                    và không xác định nguyên nhân đau.

                </p>

            </div>

        </div>
    );
}